import React, { useEffect, useState } from "react";
import api from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { Package, Wallet as WalletIcon, AlertTriangle, TrendingUp, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, CartesianGrid } from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ shipments: 0, rebills: 0, totalSpend: 0 });
  const [recent, setRecent] = useState([]);
  const [rebills, setRebills] = useState([]);

  useEffect(() => {
    (async () => {
      const [sh, rb, st] = await Promise.all([
        api.get("/shipments"),
        api.get("/billing/rebills"),
        api.get("/billing/statements"),
      ]);
      setRecent(sh.data.slice(0, 6));
      setRebills(rb.data.filter((r) => r.status === "pending"));
      const totalSpend = st.data.statements.reduce((a, s) => a + s.total, 0);
      setStats({ shipments: sh.data.length, rebills: rb.data.filter((r) => r.status === "pending").length, totalSpend });
    })();
  }, []);

  const spendChart = recent
    .slice()
    .reverse()
    .map((s, i) => ({ name: s.book_number.slice(-4), spend: s.total }));

  return (
    <DashboardLayout>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Welcome back</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-900 mt-1">
          {user?.name?.split(" ")[0] || "Shipper"} · Overview
        </h1>
      </div>

      {user?.approval_status === "pending" && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-sm p-5 sm:p-6" data-testid="pending-approval-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-display font-bold text-zinc-900">Your account is pending approval</div>
              <div className="text-sm text-zinc-700 mt-1 max-w-2xl">
                Welcome to SellePack. We review every application to protect the network and our chargeback guarantees.
                You can explore the dashboard now — as soon as you're approved, your payments wallet will be unlocked and you'll be able to load carrier accounts and book shipments. Most approvals complete within one business day.
              </div>
            </div>
          </div>
        </div>
      )}
      {user?.approval_status === "rejected" && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-sm p-5 sm:p-6" data-testid="rejected-approval-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-red-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-display font-bold text-zinc-900">Application not approved</div>
              <div className="text-sm text-zinc-700 mt-1">Contact support if you believe this is an error.</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Wallet Balance" value={`$${(user?.wallet_balance || 0).toFixed(2)}`} icon={WalletIcon} accent testId="stat-wallet" />
        <StatCard label="Total Shipments" value={stats.shipments} icon={Package} testId="stat-shipments" />
        <StatCard label="Pending Rebills" value={stats.rebills} icon={AlertTriangle} warn={stats.rebills > 0} testId="stat-rebills" />
        <StatCard label="Lifetime Spend" value={`$${stats.totalSpend.toFixed(2)}`} icon={TrendingUp} testId="stat-spend" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="md:col-span-2 bg-white border border-zinc-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Recent Shipment Volume</div>
              <div className="font-display text-xl font-bold text-zinc-900 mt-1">Spend trend</div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={spendChart}>
                <CartesianGrid vertical={false} stroke="#f4f4f5" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} style={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2 }} />
                <Bar dataKey="spend" fill="var(--brand-accent)" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-6 flex flex-col">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Quick Actions</div>
          <div className="mt-4 space-y-2 flex-1">
            <Link to="/dashboard/shipments/new" data-testid="quick-new-shipment" className="block brand-bg text-white px-4 py-3 rounded-sm hover:opacity-90 text-sm font-medium">
              + Create shipment
            </Link>
            <Link to="/dashboard/wallet" data-testid="quick-topup" className="block border border-zinc-300 text-zinc-900 px-4 py-3 rounded-sm hover:bg-zinc-50 text-sm font-medium">
              Top up wallet
            </Link>
            <Link to="/dashboard/addresses" className="block border border-zinc-300 text-zinc-900 px-4 py-3 rounded-sm hover:bg-zinc-50 text-sm font-medium">
              Address book
            </Link>
            <Link to="/dashboard/files" className="block border border-zinc-300 text-zinc-900 px-4 py-3 rounded-sm hover:bg-zinc-50 text-sm font-medium">
              File vault
            </Link>
          </div>
        </div>
      </div>

      {/* Recent shipments */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl font-bold">Recent shipments</div>
          <Link to="/dashboard/shipments" className="text-sm brand-text hover:underline">View all →</Link>
        </div>
        {recent.length === 0 ? (
          <div className="py-12 text-center text-zinc-500 text-sm">No shipments yet. <Link to="/dashboard/shipments/new" className="brand-text">Create your first</Link></div>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <th className="text-left py-2">Book #</th>
                <th className="text-left">Carrier</th>
                <th className="text-left">Receiver</th>
                <th className="text-left">Status</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((s) => (
                <tr key={s.book_number} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="py-3 font-mono-plex">{s.book_number}</td>
                  <td>{s.carrier_name}</td>
                  <td className="truncate max-w-[200px]">{s.receiver.name}, {s.receiver.city}</td>
                  <td><StatusBadge status={s.status} /></td>
                  <td className="text-right font-mono-plex">${s.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Pending rebills */}
      {rebills.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-6" data-testid="pending-rebills-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 mt-1" />
            <div className="flex-1">
              <div className="font-display font-bold text-zinc-900">{rebills.length} pending surcharge rebill{rebills.length > 1 ? "s" : ""}</div>
              <div className="text-sm text-zinc-700 mt-1">Carriers reported post-shipment surcharges. Review & charge them from Billing.</div>
              <Link to="/dashboard/billing" className="inline-block mt-3 brand-bg text-white text-sm px-4 py-2 rounded-sm hover:opacity-90">Review rebills</Link>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function StatCard({ label, value, icon: Icon, accent, warn, testId }) {
  return (
    <div className={`bg-white border rounded-sm p-5 ${warn ? "border-amber-300" : "border-zinc-200"}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <Icon size={16} strokeWidth={1.5} className={accent ? "brand-text" : "text-zinc-400"} />
      </div>
      <div className={`font-display text-3xl font-black tracking-tighter mt-3 ${warn ? "text-amber-600" : "text-zinc-900"}`}>
        {value}
      </div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    BOOKED: "bg-blue-50 text-blue-700",
    IN_TRANSIT: "bg-indigo-50 text-indigo-700",
    OUT_FOR_DELIVERY: "bg-violet-50 text-violet-700",
    DELIVERED: "bg-emerald-50 text-emerald-700",
    VOIDED: "bg-zinc-100 text-zinc-600",
  };
  return (
    <span className={`px-2 py-0.5 rounded-sm text-xs font-medium uppercase tracking-wider ${map[status] || "bg-zinc-100 text-zinc-600"}`}>
      {status}
    </span>
  );
}
